import { useState, useEffect } from 'react';
import { isAuthenticated as getIsAuthenticated } from '@marriott/mi-ui-library';
import { getPricingContentForUser } from '../modules/utils';

/**
 * usePricingContent
 *
 * Resolves the correct pricing terms string based on the current user's
 * authentication state. Safe to call during SSR — auth check runs only
 * client-side (inside useEffect) to avoid window-access errors.
 *
 * Errors thrown by isAuthenticated (e.g. Adobe Launch not yet initialised)
 * are caught and treated as unauthenticated, so callers never crash.
 *
 * @param pricingTermsGuest    - Pricing copy shown to unauthenticated users
 * @param pricingTermsSignedIn - Pricing copy shown to authenticated users
 * @returns Resolved pricing string (empty string when neither term is provided)
 */
export const usePricingContent = (pricingTermsGuest?: string, pricingTermsSignedIn?: string): string => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pricingContent, setPricingContent] = useState('');

  useEffect(() => {
    try {
      setIsAuthenticated(getIsAuthenticated());
    } catch {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    setPricingContent(
      pricingTermsGuest || pricingTermsSignedIn
        ? getPricingContentForUser(pricingTermsGuest, pricingTermsSignedIn) ?? ''
        : ''
    );
  }, [pricingTermsGuest, pricingTermsSignedIn, isAuthenticated]);

  return pricingContent;
};
